/**
 * WelcomeScreen - Initial screen with greeting and agent selection
 *
 * Based on Figma mockup: Shows greeting, agent avatars with photos
 */

import clsx from 'clsx'
import { useTranslation } from '@/i18n'
import type { AgentId } from '../../types'
import { AGENTS, AGENT_COLORS } from '../../constants'
import type { ReactNode } from 'react'

interface WelcomeScreenProps {
  activeAgent: AgentId
  onAgentSelect: (agentId: AgentId) => void
  userName?: string
  children?: ReactNode
  availableAgentIds?: AgentId[]
}

export function WelcomeScreen({
  activeAgent,
  onAgentSelect,
  userName,
  children,
  availableAgentIds,
}: WelcomeScreenProps) {
  const { t } = useTranslation()

  const visibleAgents = availableAgentIds
    ? Object.values(AGENTS).filter((agent) => availableAgentIds.includes(agent.id))
    : Object.values(AGENTS)

  return (
    <div className="flex flex-col items-center justify-center h-full px-8 py-12">
      {/* Gradient background decoration - peach/salmon matching Figma */}
      <div className="absolute top-0 left-0 right-0 h-72 bg-gradient-to-b from-[#FDCFB4] via-[#FEE8DC]/60 to-transparent pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-5xl">
        {/* Greeting */}
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-dark-text mb-10">
          {userName
            ? t('chatv2:welcome.greeting_with_name', { name: userName })
            : t('chatv2:welcome.greeting', { name: '' })}
        </h1>

        {/* Agent Selection Grid */}
        <div className="flex gap-2 mb-8">
          {visibleAgents.map((agent) => {
            const isActive = activeAgent === agent.id
            const colors = AGENT_COLORS[agent.color]

            return (
              <button
                key={agent.id}
                onClick={() => onAgentSelect(agent.id)}
                className="flex flex-col items-start gap-1.5 transition-transform hover:scale-105"
              >
                {/* Avatar with ring/border */}
                <div
                  className={clsx(
                    'w-[88px] h-[88px] rounded-full overflow-hidden transition-all mx-auto',
                    isActive
                      ? 'ring-[4px] ring-purple-400 ring-offset-2'
                      : ['border-2', colors.border]
                  )}
                >
                  <img
                    src={agent.avatarUrl}
                    alt={agent.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Description */}
                <span className="text-xs text-gray-500 dark:text-dark-text-muted text-center w-[110px]">
                  {t(`chatv2:agents.${agent.id}.description`)}
                </span>
              </button>
            )
          })}
        </div>

        {/* Children (input + quick actions) */}
        {children}
      </div>
    </div>
  )
}

export default WelcomeScreen
